import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import {
  colors,
  Encounter,
  EncounterStatus,
  EncounterType,
  EncounterTypeConfig,
  encounterTypeName,
  examTypeConfigurationFields,
  FieldMap,
  FieldTypes,
  isField,
  patientEncounterTypes,
  pregnancyEncounterTypes,
  WithId,
} from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { SimpleForm, StandaloneInput } from '../../components/forms'
import { useProfilePopoverMaxHeight } from '../../components/Users/Profile/hooks/useProfilePopoverMaxHeight'
import { useProfile } from '../../contexts/user/ProfileContext'
import { EncounterFormModal } from './EncounterFormModal'
import { EncounterSelection } from './types'

const EncounterListDivider = ({ children }: { children: string }) => (
  <Flex w="100%" px={3} py={1.5} bg="gray.100" _dark={{ bg: 'gray.800' }}>
    <Text fontSize="sm" fontWeight={500}>
      {children}
    </Text>
  </Flex>
)
const EncounterTypeButton = ({ type, onClick }: { type: EncounterType; onClick: () => void }) => (
  <Button
    key={type}
    justifyContent="flex-start"
    fontFamily="Hero-New"
    fontWeight={400}
    w="100%"
    bg="white"
    _dark={{ bg: 'gray.900' }}
    borderRadius={0}
    onClick={onClick}>
    {encounterTypeName[type]}
  </Button>
)

const SameDayConfirmStep = ({
  draft,
  onOpenDraft,
  onStartNew,
  onBack,
}: {
  draft: WithId<Encounter>
  onOpenDraft: () => void
  onStartNew: () => void
  onBack: () => void
}) => (
  <VStack spacing={3} p={4} align="stretch">
    <Alert status="warning" borderRadius={6} py={2}>
      <AlertIcon />
      <AlertDescription fontSize="sm">
        A draft antepartum encounter from today already exists for this patient.
      </AlertDescription>
    </Alert>
    <Text fontSize="xs" color="gray.500">
      Started {draft.date ?? 'today'}
      {draft.status === EncounterStatus.DRAFT ? ' — still in draft' : ''}
    </Text>
    <Button colorScheme="teal" size="sm" fontFamily="Hero-New" onClick={onOpenDraft}>
      Open Existing Draft
    </Button>
    <Button
      variant="outline"
      colorScheme="gray"
      size="sm"
      fontFamily="Hero-New"
      onClick={onStartNew}>
      Start New Encounter Anyway
    </Button>
    <Button
      variant="ghost"
      size="xs"
      leftIcon={<ArrowBackIcon />}
      onClick={onBack}
      alignSelf="flex-start"
      color="gray.500">
      Back
    </Button>
  </VStack>
)

const AddEncounterContent = ({
  isOpen,
  onClose,
  setOpenModal,
}: {
  isOpen: boolean
  onClose: () => void
  setOpenModal: (modal: EncounterSelection) => void
}) => {
  const { assessmentId, encounters } = useProfile()
  const { dataArr: encountersArr } = encounters
  const [selectedType, setSelectedType] = useState<EncounterType | null>(null)
  const [pendingSameDay, setPendingSameDay] = useState<{
    draft: WithId<Encounter>
    config: EncounterTypeConfig
  } | null>(null)

  const { maxHeight, containerRef } = useProfilePopoverMaxHeight(isOpen)

  const handleTypeClick = useCallback(
    (type: EncounterType) => {
      if (examTypeConfigurationFields[type]) {
        setSelectedType(type)
      } else {
        setOpenModal({ type, examId: null, config: null })
        onClose()
      }
    },
    [onClose, setOpenModal],
  )

  const configFieldMap = useMemo<FieldMap | null>(() => {
    if (!selectedType) return null
    const configFields = examTypeConfigurationFields[selectedType]
    if (!configFields) return null
    return { name: '', children: configFields }
  }, [selectedType])

  const singleDropdownField = useMemo(() => {
    if (!configFieldMap) return null
    const entries = Object.entries(configFieldMap.children)
    if (entries.length !== 1) return null
    const [fieldId, field] = entries[0]
    if (!isField(field) || field.type !== FieldTypes.DROPDOWN) return null
    return { fieldId, field }
  }, [configFieldMap])

  const handleConfigSubmit = useCallback(
    async (values: EncounterTypeConfig) => {
      if (!selectedType) return
      // §2.16: Before opening a new antepartum encounter, check for a same-day draft.
      if (selectedType === EncounterType.ANTEPARTUM && encountersArr) {
        const today = new Date().toISOString().slice(0, 10)
        const draft = encountersArr.find(
          enc =>
            enc.assessmentId === assessmentId &&
            enc.type === EncounterType.ANTEPARTUM &&
            enc.status === EncounterStatus.DRAFT &&
            enc.date === today,
        )
        if (draft) {
          setPendingSameDay({ draft, config: values })
          return
        }
      }
      setOpenModal({ type: selectedType, examId: null, config: values })
      onClose()
      return undefined
    },
    [onClose, selectedType, setOpenModal, assessmentId, encountersArr],
  )

  return (
    <Portal>
      <PopoverContent w="auto">
        <PopoverHeader
          bg={colors.green.hex}
          borderTopRadius={6}
          fontFamily="Comfortaa"
          py={1.5}
          fontWeight={600}
          color="white"
          textShadow="1px 1px 3px #00000066">
          New {selectedType ? encounterTypeName[selectedType] : 'Exam'}
        </PopoverHeader>
        <PopoverArrow bg={colors.green.hex} />
        <PopoverBody
          bg="gray.50"
          ref={containerRef}
          display="flex"
          flexFlow="column"
          w="300px"
          maxW="100vw"
          maxH={`${maxHeight}px`}
          h="auto"
          overflowY="auto"
          p={0}>
          <PopoverCloseButton top={1.5} borderRadius="full" bg="blackAlpha.300" color="white" />
          {pendingSameDay ? (
            <SameDayConfirmStep
              draft={pendingSameDay.draft}
              onOpenDraft={() => {
                setOpenModal({
                  type: EncounterType.ANTEPARTUM,
                  examId: pendingSameDay.draft.id,
                  config: pendingSameDay.draft.config,
                })
                onClose()
              }}
              onStartNew={() => {
                setOpenModal({
                  type: EncounterType.ANTEPARTUM,
                  examId: null,
                  config: pendingSameDay.config,
                })
                onClose()
              }}
              onBack={() => setPendingSameDay(null)}
            />
          ) : selectedType && configFieldMap ? (
            singleDropdownField ? (
              <VStack spacing={3} p={4} align="stretch">
                <StandaloneInput
                  field={singleDropdownField.field}
                  onChange={value => {
                    if (value) {
                      handleConfigSubmit({
                        [singleDropdownField.fieldId]: value,
                      } as EncounterTypeConfig)
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  leftIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedType(null)}
                  alignSelf="flex-start"
                  color="gray.500">
                  Back
                </Button>
              </VStack>
            ) : (
              <SimpleForm
                field={configFieldMap}
                submitText="Open Note"
                hasResetButton={false}
                onCancel={() => setSelectedType(null)}
                onSubmit={handleConfigSubmit}
                boxProps={{ shadow: 'none', bg: 'transparent' }}
                bodyContainerProps={{ py: 1 }}
              />
            )
          ) : (
            <VStack spacing={0} borderBottomRadius={6} align="stretch" divider={<Divider />}>
              {assessmentId ? (
                <VStack w="100%" spacing={0} divider={<Divider />}>
                  <EncounterListDivider>PREGNANCY</EncounterListDivider>
                  {pregnancyEncounterTypes
                    .filter(t => t !== EncounterType.BIRTHING_CENTER_ADMISSION)
                    .map(type => (
                      <EncounterTypeButton
                        key={type}
                        type={type}
                        onClick={() => handleTypeClick(type)}
                      />
                    ))}
                </VStack>
              ) : null}
              <EncounterListDivider>PATIENT</EncounterListDivider>
              {patientEncounterTypes.map(type => (
                <EncounterTypeButton key={type} type={type} onClick={() => handleTypeClick(type)} />
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Portal>
  )
}

export const AddEncounterPopover = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { selectedAssessment, userId: patientId } = useProfile()
  const assessmentId = selectedAssessment?.id ?? null

  const [openModal, setOpenModal] = useState<EncounterSelection | null>(null)

  const closeModal = useCallback(() => {
    setOpenModal(null)
  }, [])

  return (
    <Box>
      <Popover
        closeOnBlur={false}
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        isLazy
        onOpen={onOpen}
        strategy="fixed">
        <PopoverTrigger>
          <Button
            size="sm"
            fontWeight={300}
            fontFamily="hero-new"
            boxShadow="md"
            bg={colors.green.hex}
            color="gray.800"
            _hover={{ bg: 'green.300' }}>
            <AddIcon w={3} h={3} mr={1} color="gray.50" />
            <Text
              height="14px"
              fontFamily="Comfortaa"
              fontSize="sm"
              fontWeight={600}
              color="gray.50">
              ENCOUNTER
            </Text>
          </Button>
        </PopoverTrigger>
        <AddEncounterContent isOpen={isOpen} onClose={onClose} setOpenModal={setOpenModal} />
      </Popover>
      <EncounterFormModal
        openEncounter={openModal}
        patientId={patientId}
        assessmentId={assessmentId}
        onClose={closeModal}
        onSuccess={setOpenModal}
      />
    </Box>
  )
}
